import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where, orderBy, addDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { WeeklyTimesheet, WeeklyTimesheetEntry, EmployeeSearchResult, TimesheetEntryFormData, DailyActivity } from '../types/timesheet.ts';
import { Employee } from '../types/employee.ts';
import { Calendar, Save, Plus, Trash2, User, Building, Clock, AlertCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const TimesheetEntry: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Employee search
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [selectedEmployeeIndex, setSelectedEmployeeIndex] = useState(-1);
  const employeeInputRef = useRef<HTMLInputElement>(null);

  // Contractor selection
  const [contractors, setContractors] = useState<any[]>([]);
  const [selectedContractor, setSelectedContractor] = useState<{id: string, name: string, markupPercentage: number} | null>(null);

  // Job search and history
  const [jobHistory, setJobHistory] = useState<{ jobName: string; jobNumber: string }[]>([]);
  const [jobSearch, setJobSearch] = useState('');
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [showActivityBreakdown, setShowActivityBreakdown] = useState<{[key: number]: boolean}>({});

  // Form data
  const [formData, setFormData] = useState<TimesheetEntryFormData>({
    jobName: '',
    jobNumber: '',
    weekEnding: getDefaultWeekEnding(),
    entries: []
  });

  function getDefaultWeekEnding(): string {
    const today = new Date();
    
    // You always want the work week that ended 8+ days ago  
    // Go back 8 days to get to 9/21 from 9/29
    const previousSunday = new Date(today);
    previousSunday.setDate(today.getDate() - 8);
    return previousSunday.toISOString().split('T')[0];
  }

  useEffect(() => {
    loadEmployees();
    loadJobHistory();
    loadContractors();
    if (id) {
      loadExistingTimesheet();
    }
  }, [id]);

  const loadJobHistory = async () => {
    try {
      const timesheetsRef = collection(db, 'weeklyTimesheets');
      const q = query(timesheetsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const jobs = new Set<string>();
      const jobData: { jobName: string; jobNumber: string }[] = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.jobName && data.jobName.trim()) {
          const jobKey = `${data.jobName}|${data.jobNumber || ''}`;
          if (!jobs.has(jobKey)) {
            jobs.add(jobKey);
            jobData.push({
              jobName: data.jobName,
              jobNumber: data.jobNumber || ''
            });
          }
        }
      });
      
      console.log('Loaded job history:', jobData);
      setJobHistory(jobData.slice(0, 20)); // Keep last 20 jobs
    } catch (err) {
      console.error('Error loading job history:', err);
    }
  };

  const loadContractors = async () => {
    try {
      const contractorsRef = collection(db, 'contractors');
      const q = query(contractorsRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      
      const contractorData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.companyName || data.name || 'Unknown',
          // For now, use default markup percentages - this will be configurable later
          markupPercentage: data.markupPercentage || 4.00 // Default to 4.00
        };
      });
      
      // Add default contractors if none exist
      if (contractorData.length === 0) {
        contractorData.push(
          { id: 'cec', name: 'CEC', markupPercentage: 4.00 },
          { id: 'reliable', name: 'Reliable', markupPercentage: 4.50 },
          { id: 'btc', name: 'BTC', markupPercentage: 25.00 }
        );
      }
      
      setContractors(contractorData);
    } catch (err) {
      console.error('Error loading contractors:', err);
      // Fallback to default contractors
      setContractors([
        { id: 'cec', name: 'CEC', markupPercentage: 4.00 },
        { id: 'reliable', name: 'Reliable', markupPercentage: 4.50 },
        { id: 'btc', name: 'BTC', markupPercentage: 25.00 }
      ]);
    }
  };

  const loadEmployees = async () => {
    try {
      const employeesRef = collection(db, 'employees');
      // Remove ordering to avoid issues with missing fields
      const q = query(employeesRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);
      
      const employeeData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Ensure we have both name formats for compatibility
          name: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          firstName: data.firstName || data.name?.split(' ')[0] || '',
          lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          hireDate: data.hireDate?.toDate(),
          terminationDate: data.terminationDate?.toDate(),
          licenseExpiration: data.licenseExpiration?.toDate(),
        };
      }) as Employee[];

      // Sort by name after loading to handle both name formats
      employeeData.sort((a, b) => {
        const nameA = a.firstName || a.name || '';
        const nameB = b.firstName || b.name || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });

      setEmployees(employeeData);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    }
  };

  const loadExistingTimesheet = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const timesheetRef = doc(db, 'weeklyTimesheets', id);
      const timesheetSnap = await getDoc(timesheetRef);
      
      if (timesheetSnap.exists()) {
        const data = timesheetSnap.data();
        setFormData({
          jobName: data.jobName,
          jobNumber: data.jobNumber || '',
          weekEnding: data.weekEnding?.toDate().toISOString().split('T')[0] || getDefaultWeekEnding(),
          entries: data.entries || []
        });
        
        // Set the selected contractor if it exists in the data
        if (data.contractorId && data.contractorName) {
          setSelectedContractor({
            id: data.contractorId,
            name: data.contractorName,
            markupPercentage: data.markupPercentage || 4.00
          });
        }
      }
    } catch (err) {
      console.error('Error loading timesheet:', err);
      setError('Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredJobs = () => {
    if (!jobSearch) return [];
    
    const searchLower = jobSearch.toLowerCase();
    return jobHistory
      .filter(job => 
        job.jobName.toLowerCase().includes(searchLower) ||
        job.jobNumber.toLowerCase().includes(searchLower)
      )
      .slice(0, 10);
  };

  const handleJobSelect = (job: { jobName: string; jobNumber: string }) => {
    setFormData(prev => ({
      ...prev,
      jobName: job.jobName,
      jobNumber: job.jobNumber
    }));
    setJobSearch(job.jobName);
    setShowJobDropdown(false);
  };

  const getFilteredEmployees = (): EmployeeSearchResult[] => {
    if (!employeeSearch) return [];
    
    const searchLower = employeeSearch.toLowerCase();
    return employees
      .filter(emp => {
        const fullName = emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
        const firstName = emp.firstName || '';
        const lastName = emp.lastName || '';
        
        return fullName.toLowerCase().includes(searchLower) ||
               firstName.toLowerCase().includes(searchLower) ||
               lastName.toLowerCase().includes(searchLower);
      })
      .map(emp => ({
        id: emp.id,
        name: emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
        rates: {
          cec: emp.rates?.cec || emp.cecRate,
          reliable: emp.rates?.reliable || emp.reliableRate,
          btc: emp.rates?.btc || emp.btcRate
        },
        isActive: emp.status === 'active'
      }))
      .slice(0, 10);
  };

  const handleEmployeeSelect = (employee: EmployeeSearchResult) => {
    // Check if employee already exists in this timesheet
    const existingEntry = formData.entries.find(entry => entry.employeeId === employee.id);
    
    if (existingEntry) {
      setError(`${employee.name} is already added to this timesheet. You can edit their hours above, or create a separate timesheet for a different job.`);
      setEmployeeSearch('');
      setShowEmployeeDropdown(false);
      setSelectedEmployeeIndex(-1);
      return;
    }

    const newEntry: WeeklyTimesheetEntry = {
      employeeId: employee.id,
      employeeName: employee.name,
      hours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      },
      regularHours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      },
      overtimeHours: {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0
      },
      activities: {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
      },
      activityCode: '',
      activityDescription: ''
    };

    setFormData(prev => ({
      ...prev,
      entries: [...prev.entries, newEntry] // Add to end for easier workflow
    }));

    setEmployeeSearch('');
    setShowEmployeeDropdown(false);
    setSelectedEmployeeIndex(-1);
    
    // Clear any previous error
    if (error && error.includes('already added')) {
      setError(null);
    }
  };

  const handleEmployeeSearchKeyDown = (e: React.KeyboardEvent) => {
    const filteredEmployees = getFilteredEmployees();
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedEmployeeIndex(prev => 
          prev < filteredEmployees.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedEmployeeIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedEmployeeIndex >= 0 && filteredEmployees[selectedEmployeeIndex]) {
          handleEmployeeSelect(filteredEmployees[selectedEmployeeIndex]);
        }
        break;
      case 'Escape':
        setShowEmployeeDropdown(false);
        setSelectedEmployeeIndex(-1);
        break;
    }
  };

  const updateEntryHours = (entryIndex: number, day: keyof WeeklyTimesheetEntry['hours'], hours: number) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map((entry, index) => 
        index === entryIndex 
          ? { ...entry, hours: { ...entry.hours, [day]: hours } }
          : entry
      )
    }));
  };

  const updateEntryRegularHours = (entryIndex: number, day: keyof WeeklyTimesheetEntry['hours'], hours: number) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map((entry, index) => 
        index === entryIndex 
          ? { 
              ...entry, 
              regularHours: { 
                ...entry.regularHours, 
                [day]: hours 
              } 
            }
          : entry
      )
    }));
  };

  const updateEntryOvertimeHours = (entryIndex: number, day: keyof WeeklyTimesheetEntry['hours'], hours: number) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map((entry, index) => 
        index === entryIndex 
          ? { 
              ...entry, 
              overtimeHours: { 
                ...entry.overtimeHours, 
                [day]: hours 
              } 
            }
          : entry
      )
    }));
  };

  const updateEntryField = (entryIndex: number, field: 'activityCode' | 'activityDescription', value: string) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map((entry, index) => 
        index === entryIndex 
          ? { ...entry, [field]: value }
          : entry
      )
    }));
  };

  const updateDayActivities = (entryIndex: number, day: keyof WeeklyTimesheetEntry['hours'], activities: DailyActivity[]) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map((entry, index) => 
        index === entryIndex 
          ? { 
              ...entry, 
              activities: {
                ...entry.activities,
                [day]: activities
              }
            }
          : entry
      )
    }));
  };

  const applyActivityToAllDays = (entryIndex: number, sourceDay: keyof WeeklyTimesheetEntry['hours']) => {
    const entry = formData.entries[entryIndex];
    const sourceActivities = entry.activities[sourceDay] || [];
    
    if (sourceActivities.length === 0) {
      setError('No activities to copy from this day');
      return;
    }

    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map((entryItem, index) => {
        if (index !== entryIndex) return entryItem;
        
        const updatedActivities: typeof entryItem.activities = { ...entryItem.activities };
        
        days.forEach(day => {
          if (day !== sourceDay && entryItem.hours[day] > 0) {
            // Scale activities proportionally to the day's hours
            const dayHours = entryItem.hours[day];
            const sourceHours = entryItem.hours[sourceDay];
            const scaleFactor = sourceHours > 0 ? dayHours / sourceHours : 1;
            
            updatedActivities[day] = sourceActivities.map(activity => ({
              ...activity,
              hours: Math.min(activity.hours * scaleFactor, dayHours)
            }));
          }
        });
        
        return {
          ...entryItem,
          activities: updatedActivities
        };
      })
    }));
  };

  const removeEntry = (entryIndex: number) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.filter((_, index) => index !== entryIndex)
    }));
  };

  const getTotalHours = (entry: WeeklyTimesheetEntry): number => {
    return Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
  };

  const getTotalRegularHours = (entry: WeeklyTimesheetEntry): number => {
    return entry.regularHours ? Object.values(entry.regularHours).reduce((sum, hours) => sum + hours, 0) : 0;
  };

  const getTotalOvertimeHours = (entry: WeeklyTimesheetEntry): number => {
    return entry.overtimeHours ? Object.values(entry.overtimeHours).reduce((sum, hours) => sum + hours, 0) : 0;
  };

  // Function to find existing timesheet for same week/job
  const findExistingTimesheet = async (jobName: string, jobNumber: string, weekEnding: Date): Promise<WeeklyTimesheet | null> => {
    try {
      const timesheetsRef = collection(db, 'weeklyTimesheets');
      
      // Create query to find timesheets with same job name and week ending
      // We'll compare dates by converting to string format to avoid timezone issues
      const weekEndingStr = weekEnding.toISOString().split('T')[0];
      
      const q = query(timesheetsRef, where('jobName', '==', jobName));
      const snapshot = await getDocs(q);
      
      // Filter results to match job number and week ending date
      const matchingTimesheets = snapshot.docs.filter(doc => {
        const data = doc.data();
        const docWeekEnding = data.weekEnding?.toDate();
        const docWeekEndingStr = docWeekEnding?.toISOString().split('T')[0];
        
        // Match job number (both empty/undefined should match)
        const jobNumberMatch = (data.jobNumber || '') === (jobNumber || '');
        
        // Match week ending date
        const dateMatch = docWeekEndingStr === weekEndingStr;
        
        return jobNumberMatch && dateMatch;
      });
      
      if (matchingTimesheets.length > 0) {
        const timesheetDoc = matchingTimesheets[0];
        return {
          id: timesheetDoc.id,
          ...timesheetDoc.data(),
          weekEnding: timesheetDoc.data().weekEnding?.toDate() || new Date(),
          createdAt: timesheetDoc.data().createdAt?.toDate() || new Date(),
          updatedAt: timesheetDoc.data().updatedAt?.toDate() || new Date()
        } as WeeklyTimesheet;
      }
      
      return null;
    } catch (err) {
      console.error('Error finding existing timesheet:', err);
      return null;
    }
  };

  // Function to combine employee entries, avoiding duplicates
  const combineTimesheetEntries = (existingEntries: WeeklyTimesheetEntry[], newEntries: WeeklyTimesheetEntry[]): WeeklyTimesheetEntry[] => {
    const combined = [...existingEntries];
    
    for (const newEntry of newEntries) {
      // Check if employee already exists in the timesheet
      const existingIndex = combined.findIndex(entry => entry.employeeId === newEntry.employeeId);
      
      if (existingIndex >= 0) {
        // Employee exists, combine their hours
        const existing = combined[existingIndex];
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
        
        // Combine regular hours
        const combinedHours: WeeklyTimesheetEntry['hours'] = { ...existing.hours };
        const combinedRegularHours: WeeklyTimesheetEntry['regularHours'] = existing.regularHours ? { ...existing.regularHours } : {
          monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0
        };
        const combinedOvertimeHours: WeeklyTimesheetEntry['overtimeHours'] = existing.overtimeHours ? { ...existing.overtimeHours } : {
          monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0
        };
        const combinedActivities: WeeklyTimesheetEntry['activities'] = { ...existing.activities };
        
        for (const day of days) {
          // Add hours
          combinedHours[day] = (combinedHours[day] || 0) + (newEntry.hours[day] || 0);
          
          // Add regular/overtime hours if available
          if (newEntry.regularHours) {
            combinedRegularHours[day] = (combinedRegularHours[day] || 0) + (newEntry.regularHours[day] || 0);
          }
          if (newEntry.overtimeHours) {
            combinedOvertimeHours[day] = (combinedOvertimeHours[day] || 0) + (newEntry.overtimeHours[day] || 0);
          }
          
          // Combine activities
          const existingDayActivities = existing.activities?.[day] || [];
          const newDayActivities = newEntry.activities?.[day] || [];
          
          if (newDayActivities.length > 0) {
            combinedActivities[day] = [...existingDayActivities, ...newDayActivities];
          }
        }
        
        combined[existingIndex] = {
          ...existing,
          hours: combinedHours,
          regularHours: combinedRegularHours,
          overtimeHours: combinedOvertimeHours,
          activities: combinedActivities,
          // Update activity code/description if new entry has them
          activityCode: newEntry.activityCode || existing.activityCode,
          activityDescription: newEntry.activityDescription || existing.activityDescription
        };
      } else {
        // New employee, add to list
        combined.push(newEntry);
      }
    }
    
    return combined;
  };

  const handleSave = async (status: 'draft' | 'completed' = 'draft') => {
    if (!currentUser) return;

    // Validation
    if (!formData.jobName.trim()) {
      setError('Job name is required');
      return;
    }
    
    if (!selectedContractor) {
      setError('Contractor selection is required');
      return;
    }

    if (formData.entries.length === 0) {
      setError('At least one employee entry is required');
      return;
    }

    // Create date properly to avoid timezone issues
    const [year, month, day] = formData.weekEnding.split('-').map(Number);
    const weekEndingDate = new Date(year, month - 1, day); // month is 0-indexed
    if (weekEndingDate.getDay() !== 0) {
      setError('Week ending date must be a Sunday');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (id) {
        // Update existing timesheet
        const timesheetData: Omit<WeeklyTimesheet, 'id'> = {
          jobName: formData.jobName.trim(),
          jobNumber: formData.jobNumber.trim() || '',
          weekEnding: weekEndingDate,
          entries: formData.entries,
          status,
          contractorId: selectedContractor.id,
          contractorName: selectedContractor.name,
          markupPercentage: selectedContractor.markupPercentage,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: currentUser.uid
        };

        await updateDoc(doc(db, 'weeklyTimesheets', id), {
          ...timesheetData,
          updatedAt: new Date()
        });
        setSuccess(`Timesheet ${status === 'completed' ? 'completed' : 'saved'} successfully`);
        
        // Redirect to timesheets page if completing
        if (status === 'completed') {
          setTimeout(() => {
            navigate('/timesheets');
          }, 1500);
        }
      } else {
        // Check for existing timesheet for the same week/job
        const existingTimesheet = await findExistingTimesheet(
          formData.jobName.trim(),
          formData.jobNumber.trim(),
          weekEndingDate
        );

        if (existingTimesheet) {
          // Combine with existing timesheet
          const combinedEntries = combineTimesheetEntries(existingTimesheet.entries, formData.entries);
          
          // Update the existing timesheet
          await updateDoc(doc(db, 'weeklyTimesheets', existingTimesheet.id), {
            entries: combinedEntries,
            status: status, // Update status to the new one being saved
            updatedAt: new Date(),
            // Keep existing contractor info unless it's different
            contractorId: selectedContractor.id,
            contractorName: selectedContractor.name,
            markupPercentage: selectedContractor.markupPercentage
          });
          
          setSuccess(`Timesheet combined with existing timesheet for ${formData.jobName} (Week ending ${weekEndingDate.toLocaleDateString()})`);
          
          // Redirect to timesheets page if completing, otherwise to edit page
          if (status === 'completed') {
            setTimeout(() => {
              navigate('/timesheets');
            }, 1500);
          } else {
            navigate(`/timesheets/entry/${existingTimesheet.id}`);
          }
        } else {
          // Create new timesheet
          const timesheetData: Omit<WeeklyTimesheet, 'id'> = {
            jobName: formData.jobName.trim(),
            jobNumber: formData.jobNumber.trim() || '',
            weekEnding: weekEndingDate,
            entries: formData.entries,
            status,
            contractorId: selectedContractor.id,
            contractorName: selectedContractor.name,
            markupPercentage: selectedContractor.markupPercentage,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: currentUser.uid
          };

          const docRef = await addDoc(collection(db, 'weeklyTimesheets'), timesheetData);
          setSuccess(`Timesheet ${status === 'completed' ? 'completed' : 'saved'} successfully`);
          
          // Redirect to timesheets page if completing, otherwise to edit page
          if (status === 'completed') {
            setTimeout(() => {
              navigate('/timesheets');
            }, 1500);
          } else {
            navigate(`/timesheets/entry/${docRef.id}`);
          }
        }
      }
    } catch (err) {
      console.error('Error saving timesheet:', err);
      setError('Failed to save timesheet');
    } finally {
      setSaving(false);
    }
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {id ? 'Edit Timesheet' : 'New Timesheet Entry'}
            </h1>
            <p className="text-gray-600 mt-1">
              Enter weekly timesheet data for job site employees
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/timesheets')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={() => handleSave('completed')}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Complete & Save'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      {/* Job Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <Building className="h-5 w-5 mr-2" />
          Job Information
        </h2>
        
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Name *
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.jobName || jobSearch}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, jobName: value }));
                  setJobSearch(value);
                  setShowJobDropdown(true);
                }}
                onFocus={() => {
                  setJobSearch(formData.jobName || '');
                  setShowJobDropdown(true);
                }}
                onBlur={() => {
                  // Delay hiding to allow for clicks
                  setTimeout(() => setShowJobDropdown(false), 200);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter job name or search previous jobs..."
                required
              />
              
              {showJobDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {(jobSearch && jobSearch.length > 0 ? getFilteredJobs() : jobHistory.slice(0, 10)).map((job, index) => (
                    <div
                      key={index}
                      onClick={() => handleJobSelect(job)}
                      className="px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                    >
                      <div className="font-medium text-gray-900">{job.jobName}</div>
                      {job.jobNumber && (
                        <div className="text-sm text-gray-500">#{job.jobNumber}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Number
            </label>
            <input
              type="text"
              value={formData.jobNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, jobNumber: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Optional job number"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contractor *
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={selectedContractor?.id || ''}
                onChange={(e) => {
                  const contractor = contractors.find(c => c.id === e.target.value);
                  setSelectedContractor(contractor || null);
                }}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select Contractor</option>
                {contractors.map((contractor) => (
                  <option key={contractor.id} value={contractor.id}>
                    {contractor.name} ({contractor.markupPercentage === 25 ? '25%' : `${contractor.markupPercentage.toFixed(2)}`} markup)
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Week Ending Date (Sunday) *
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={formData.weekEnding}
                onChange={(e) => {
                  // Create date properly to avoid timezone issues
                  const dateString = e.target.value;
                  if (dateString) {
                    const [year, month, day] = dateString.split('-').map(Number);
                    const selectedDate = new Date(year, month - 1, day); // month is 0-indexed
                    if (selectedDate.getDay() !== 0) {
                      setError('Week ending date must be a Sunday');
                    } else {
                      setError(null);
                      setFormData(prev => ({ ...prev, weekEnding: e.target.value }));
                    }
                  } else {
                    setFormData(prev => ({ ...prev, weekEnding: e.target.value }));
                  }
                }}
                className="pl-10 w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>
        </div>
      </div>

      {/* Employee Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Add Employees
        </h2>
        
        <div className="relative">
          <input
            ref={employeeInputRef}
            type="text"
            value={employeeSearch}
            onChange={(e) => {
              setEmployeeSearch(e.target.value);
              setShowEmployeeDropdown(e.target.value.length > 0);
              setSelectedEmployeeIndex(-1);
            }}
            onKeyDown={handleEmployeeSearchKeyDown}
            onFocus={() => setShowEmployeeDropdown(employeeSearch.length > 0)}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
            placeholder="Start typing employee name to add them to this timesheet..."
          />
          
          {showEmployeeDropdown && getFilteredEmployees().length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {getFilteredEmployees().map((employee, index) => (
                <div
                  key={employee.id}
                  onClick={() => handleEmployeeSelect(employee)}
                  className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                    index === selectedEmployeeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-gray-900">{employee.name}</div>
                  <div className="text-sm text-gray-500">
                    Rates: CEC: ${employee.rates.cec || 'N/A'}, REL: ${employee.rates.reliable || 'N/A'}, BTC: ${employee.rates.btc || 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Timesheet Entries */}
      {formData.entries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Weekly Hours Entry
          </h2>
          
          <div className="space-y-4">
            {formData.entries.map((entry, entryIndex) => (
              <div key={entryIndex} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{entry.employeeName}</h3>
                    <div className="text-sm text-gray-500 space-y-1">
                      <div>Regular: {getTotalRegularHours(entry).toFixed(1)}h | OT: {getTotalOvertimeHours(entry).toFixed(1)}h | Total: {getTotalHours(entry).toFixed(1)}h</div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeEntry(entryIndex)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Remove employee"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Hours Grid - Fixed Layout */}
                <div className="mb-3">
                  <div className="space-y-2">
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-2">
                      {days.map((day, dayIndex) => (
                        <div key={day} className="text-center font-medium text-gray-700 text-sm">
                          {dayLabels[dayIndex]}
                        </div>
                      ))}
                    </div>
                    
                    {/* Regular Hours Row */}
                    <div className="grid grid-cols-7 gap-2">
                      {days.map((day, dayIndex) => {
                        const regTabIndex = (entryIndex * 100) + (dayIndex * 2) + 1;
                        return (
                          <div key={day} className="space-y-1">
                            <label className="block text-xs font-medium text-blue-600 text-center">
                              Regular
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="24"
                              step="0.25"
                              tabIndex={regTabIndex}
                              value={entry.regularHours?.[day] || 0}
                              onChange={(e) => {
                                const regHours = parseFloat(e.target.value) || 0;
                                updateEntryRegularHours(entryIndex, day, regHours);
                                const otHours = entry.overtimeHours?.[day] || 0;
                                updateEntryHours(entryIndex, day, regHours + otHours);
                              }}
                              onFocus={(e) => e.target.select()}
                              className="w-full px-2 py-1 text-center text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0"
                            />
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Overtime Hours Row */}
                    <div className="grid grid-cols-7 gap-2">
                      {days.map((day, dayIndex) => {
                        const otTabIndex = (entryIndex * 100) + (dayIndex * 2) + 2;
                        return (
                          <div key={day} className="space-y-1">
                            <label className="block text-xs font-medium text-orange-600 text-center">
                              OT
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="24"
                              step="0.25"
                              tabIndex={otTabIndex}
                              value={entry.overtimeHours?.[day] || 0}
                              onChange={(e) => {
                                const otHours = parseFloat(e.target.value) || 0;
                                updateEntryOvertimeHours(entryIndex, day, otHours);
                                const regHours = entry.regularHours?.[day] || 0;
                                updateEntryHours(entryIndex, day, regHours + otHours);
                              }}
                              onFocus={(e) => e.target.select()}
                              className="w-full px-2 py-1 text-center text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                              placeholder="0"
                            />
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Total Hours Row */}
                    <div className="grid grid-cols-7 gap-2">
                      {days.map((day, dayIndex) => (
                        <div key={day} className="space-y-1">
                          <label className="block text-xs font-medium text-gray-500 text-center">
                            Total
                          </label>
                          <div className="w-full px-2 py-1 text-center bg-gray-50 border border-gray-200 rounded text-sm font-medium text-gray-700">
                            {((entry.regularHours?.[day] || 0) + (entry.overtimeHours?.[day] || 0)).toFixed(1)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Daily Activity Breakdown - Hidden by default */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      type="button"
                      onClick={() => setShowActivityBreakdown(prev => ({
                        ...prev,
                        [entryIndex]: !prev[entryIndex]
                      }))}
                      className="flex items-center text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors"
                    >
                      <svg 
                        className={`h-4 w-4 mr-2 transition-transform ${showActivityBreakdown[entryIndex] ? 'rotate-90' : ''}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Daily Activity Breakdown {showActivityBreakdown[entryIndex] ? '(Hide)' : '(Show)'}
                    </button>
                    {showActivityBreakdown[entryIndex] && (
                      <div className="text-xs text-gray-500">
                        Click "Copy to All" to apply a day's activities to other days with hours
                      </div>
                    )}
                  </div>
                  
                  {showActivityBreakdown[entryIndex] && (
                    <div className="grid grid-cols-7 gap-1">
                    {days.map((day, dayIndex) => {
                      const dayHours = entry.hours[day];
                      const dayActivities = entry.activities?.[day] || [];
                      const totalActivityHours = dayActivities.reduce((sum, act) => sum + act.hours, 0);
                      const remainingHours = dayHours - totalActivityHours;
                      
                      return (
                        <div key={day} className="border border-gray-200 rounded-md p-2">
                          <div className="text-xs font-medium text-center text-gray-700 mb-1">
                            {dayLabels[dayIndex]}
                          </div>
                          <div className="text-xs text-center text-gray-500 mb-2">
                            {dayHours}h total
                          </div>
                          
                          {/* Copy to All Days button - only show if this day has activities */}
                          {dayActivities.length > 0 && (
                            <button
                              type="button"
                              onClick={() => applyActivityToAllDays(entryIndex, day)}
                              className="w-full text-xs text-purple-600 hover:text-purple-800 border border-dashed border-purple-300 rounded p-1 hover:bg-purple-50 mb-2"
                              title="Copy this day's activities to all other days with hours"
                            >
                              Copy to All
                            </button>
                          )}
                          
                          {/* Activity entries for this day */}
                          <div className="space-y-2">
                            {dayActivities.map((activity, actIndex) => (
                              <div key={actIndex} className="bg-gray-50 p-2 rounded text-xs">
                                <input
                                  type="text"
                                  value={activity.activityCode}
                                  onChange={(e) => {
                                    const newActivities = [...dayActivities];
                                    newActivities[actIndex] = { ...activity, activityCode: e.target.value };
                                    updateDayActivities(entryIndex, day, newActivities);
                                  }}
                                  placeholder="Code"
                                  className="w-full px-1 py-1 text-xs border border-gray-300 rounded mb-1"
                                />
                                <input
                                  type="number"
                                  step="0.25"
                                  min="0"
                                  max={dayHours}
                                  value={activity.hours}
                                  onChange={(e) => {
                                    const newActivities = [...dayActivities];
                                    newActivities[actIndex] = { ...activity, hours: parseFloat(e.target.value) || 0 };
                                    updateDayActivities(entryIndex, day, newActivities);
                                  }}
                                  placeholder="Hours"
                                  className="w-full px-1 py-1 text-xs border border-gray-300 rounded mb-1"
                                />
                                <input
                                  type="text"
                                  value={activity.activityDescription}
                                  onChange={(e) => {
                                    const newActivities = [...dayActivities];
                                    newActivities[actIndex] = { ...activity, activityDescription: e.target.value };
                                    updateDayActivities(entryIndex, day, newActivities);
                                  }}
                                  placeholder="Description"
                                  className="w-full px-1 py-1 text-xs border border-gray-300 rounded mb-1"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newActivities = dayActivities.filter((_, i) => i !== actIndex);
                                    updateDayActivities(entryIndex, day, newActivities);
                                  }}
                                  className="text-xs text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            
                            {/* Add activity button */}
                            {dayHours > 0 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newActivity = {
                                    hours: Math.max(0, remainingHours),
                                    activityCode: '',
                                    activityDescription: ''
                                  };
                                  updateDayActivities(entryIndex, day, [...dayActivities, newActivity]);
                                }}
                                className="w-full text-xs text-blue-600 hover:text-blue-800 border border-dashed border-blue-300 rounded p-1 hover:bg-blue-50"
                              >
                                + Add Activity
                              </button>
                            )}
                            
                            {/* Show remaining hours */}
                            {remainingHours > 0 && (
                              <div className="text-xs text-orange-600 text-center">
                                {remainingHours.toFixed(2)}h unassigned
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {formData.entries.length === 0 && (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No employees added</h3>
          <p className="mt-1 text-sm text-gray-500">
            Start typing an employee name above to add them to this timesheet.
          </p>
        </div>
      )}
    </div>
  );
};

export default TimesheetEntry;
